import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const EFFECTIVE_DATE = 'April 6, 2026';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6">
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-sky-500 hover:text-sky-400 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="card p-8 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>

        {/* Introduction */}
        <div className="card p-8 sm:p-10">
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            AKA IND Technologies ("AKA IND", "we", "us", or "our") operates FLARE Social
            (flare-social.com), a SaaS social media scheduling and publishing platform.
            This Privacy Policy explains how we collect, use, store, and protect your
            information when you use our service. By using FLARE Social, you agree to the
            practices described in this policy.
          </p>
        </div>

        {/* 1. What Data We Collect */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            1. What Data We Collect
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            We collect the following categories of information to provide and improve the
            FLARE Social service:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Account Information:</span>{' '}
              Your name, email address, password (hashed), profile picture, and billing
              details when you create an account or subscribe to a paid plan.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Social Account Tokens:</span>{' '}
              OAuth access tokens and refresh tokens for social media platforms you connect
              (e.g., TikTok, Instagram, YouTube, LinkedIn, Twitter, Facebook, Pinterest).
              These tokens allow us to publish content and retrieve analytics on your behalf.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Content:</span>{' '}
              Text, images, videos, captions, hashtags, and other media you upload, create,
              or schedule through the platform, including content generated using our
              AI features.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Analytics Data:</span>{' '}
              Engagement metrics, impressions, follower counts, and performance data
              retrieved from connected social media platforms to populate your analytics
              dashboard.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Usage Data:</span>{' '}
              Information about how you interact with the service, including pages visited,
              features used, timestamps, device type, browser, IP address, and referral
              sources.
            </li>
          </ul>
        </div>

        {/* 2. How We Use Your Data */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            2. How We Use Your Data
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            We use the data we collect for the following purposes:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Scheduling and Publishing:</span>{' '}
              To schedule, queue, and publish your content to connected social media
              platforms at the times you specify.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">AI Content Generation:</span>{' '}
              To generate captions, hashtags, and content suggestions using AI models.
              Content you provide as input may be sent to our AI provider (Anthropic Claude)
              for processing. We do not use your content to train AI models.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Analytics and Reporting:</span>{' '}
              To aggregate and display performance metrics from your connected social
              accounts within the FLARE Social dashboard.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Account Management:</span>{' '}
              To authenticate you, manage your subscription, process payments, and
              communicate service-related updates.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Service Improvement:</span>{' '}
              To understand usage patterns and improve the reliability, performance, and
              features of the platform.
            </li>
          </ul>
        </div>

        {/* 3. Third-Party Services */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            3. Third-Party Services
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            FLARE Social integrates with the following third-party services. Each has its
            own privacy policy, and we encourage you to review them:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Cloudflare:</span>{' '}
              Infrastructure hosting, CDN, DNS, and edge computing (Workers, D1 database,
              R2 storage).
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Stripe:</span>{' '}
              Payment processing for subscriptions and billing. We do not store your full
              credit card number; payment details are handled entirely by Stripe.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Google (YouTube):</span>{' '}
              OAuth authentication and API access for YouTube publishing and analytics.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Meta (Instagram, Facebook):</span>{' '}
              OAuth authentication and API access for Instagram and Facebook publishing
              and analytics.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">TikTok:</span>{' '}
              OAuth authentication and API access for TikTok publishing and analytics.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">LinkedIn:</span>{' '}
              OAuth authentication and API access for LinkedIn publishing and analytics.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Twitter (X):</span>{' '}
              OAuth authentication and API access for Twitter publishing and analytics.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Pinterest:</span>{' '}
              OAuth authentication and API access for Pinterest publishing and analytics.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">GitHub:</span>{' '}
              OAuth authentication for account sign-in.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Anthropic (Claude):</span>{' '}
              AI language model used for content generation features such as caption
              writing and content suggestions. Text inputs are sent to Anthropic for
              processing but are not used to train their models.
            </li>
          </ul>
        </div>

        {/* 4. Data Storage and Security */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            4. Data Storage and Security
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            Your data is stored using Cloudflare infrastructure:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Database:</span>{' '}
              Account data, scheduling records, and metadata are stored in Cloudflare D1
              (SQLite-based edge database). Data is encrypted at rest.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Media Storage:</span>{' '}
              Uploaded images, videos, and other media files are stored in Cloudflare R2
              (S3-compatible object storage). Data is encrypted at rest.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Encryption:</span>{' '}
              All data is transmitted over TLS (HTTPS). Sensitive values such as OAuth
              tokens are encrypted before being stored in the database.
            </li>
          </ul>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4">
            We implement industry-standard security measures to protect your data. However,
            no method of electronic storage or transmission is 100% secure, and we cannot
            guarantee absolute security.
          </p>
        </div>

        {/* 5. Cookies */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            5. Cookies
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            FLARE Social uses a minimal set of cookies necessary for the operation of the
            service:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Session Cookies:</span>{' '}
              Used to maintain your authenticated session while using the platform. These
              expire when you log out or after a period of inactivity.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">OAuth State Cookies:</span>{' '}
              Temporary cookies used during the OAuth flow when connecting social media
              accounts. These are short-lived and deleted once the connection is established.
            </li>
          </ul>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4">
            We do not use advertising cookies, tracking pixels, or third-party analytics
            cookies.
          </p>
        </div>

        {/* 6. Your Rights */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            6. Your Rights
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            You have the following rights regarding your personal data:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Access:</span>{' '}
              You may request a copy of the personal data we hold about you at any time.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Deletion:</span>{' '}
              You may request the deletion of your account and all associated personal
              data. Upon request, we will delete your data within 30 days, except where
              retention is required by law.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Export:</span>{' '}
              You may request an export of your data in a machine-readable format (JSON).
              This includes your account information, scheduled content, and stored media.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Correction:</span>{' '}
              You may update or correct your personal information at any time through
              your account settings.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Revoke Social Connections:</span>{' '}
              You may disconnect any linked social media account at any time through the
              Integrations page. This revokes the stored OAuth tokens.
            </li>
          </ul>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4">
            To exercise any of these rights, contact us at{' '}
            <a
              href="mailto:privacy@flare-social.com"
              className="text-sky-500 hover:text-sky-400 transition-colors"
            >
              privacy@flare-social.com
            </a>.
          </p>
        </div>

        {/* 7. Data Retention */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            7. Data Retention
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            We retain your data for as long as your account is active or as needed to
            provide the service. If you delete your account, we will remove your personal
            data within 30 days, except where we are required to retain certain records
            for legal, tax, or compliance purposes. Anonymized and aggregated data that
            cannot be used to identify you may be retained indefinitely for analytical
            purposes.
          </p>
        </div>

        {/* 8. Children's Privacy */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            8. Children's Privacy
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            FLARE Social is not intended for use by individuals under the age of 16. We
            do not knowingly collect personal data from children. If we become aware that
            a child under 16 has provided us with personal data, we will take steps to
            delete that information promptly.
          </p>
        </div>

        {/* 9. Changes to This Policy */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            9. Changes to This Policy
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            We may update this Privacy Policy from time to time. When we make material
            changes, we will notify you by email or by posting a notice on the platform.
            Your continued use of the service after such changes constitutes acceptance
            of the updated policy. We encourage you to review this page periodically.
          </p>
        </div>

        {/* 10. Contact */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            10. Contact
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            If you have any questions or concerns about this Privacy Policy or our data
            practices, please contact us:
          </p>
          <div className="mt-4 text-gray-600 dark:text-gray-400 leading-relaxed">
            <p className="font-medium text-gray-900 dark:text-white">AKA IND Technologies</p>
            <p>
              Email:{' '}
              <a
                href="mailto:privacy@flare-social.com"
                className="text-sky-500 hover:text-sky-400 transition-colors"
              >
                privacy@flare-social.com
              </a>
            </p>
            <p>
              Website:{' '}
              <a
                href="https://akaind.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-500 hover:text-sky-400 transition-colors"
              >
                akaind.ca
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
